import React, { createContext, useReducer, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import locales from '@spa/shared/constants/locales';
import { ProfileContext } from '@spa/shared/contexts/profile';
import { useSearchHistory } from '@spa/shared/hooks/search/history';
import { handleMultipleFields, handlePathToQuery } from '@spa/shared/utils/queryParams';
import { resultsTransformer, reducer, SET_RESULTS, INITIAL_LOAD } from './results';
import { offers as offersAction, residencesOffers } from '@spa/shared/clients/search';
import { locationToSearch } from '@spa/shared/utils/location';
import { useDebounce } from '@spa/shared/hooks';

const RESULTS_LIMIT = 42;
const ResourcesContext = createContext();
const SOURCE = 'spa';

const ResourcesProvider = ({
  children,
  offers = [],
  location = {},
  offersKey,
  isResidences = false,
  shouldLoad = true,
  staticOffer = null
}) => {
  const router = useRouter();
  const debouncedQueryParams = useDebounce(router.query, 500);
  const { profile } = useContext(ProfileContext);
  const {
    i18n: { language }
  } = useTranslation();

  const initialState = {
    ...resultsTransformer(offers, { limit: RESULTS_LIMIT, key: offersKey }),
    limit: RESULTS_LIMIT,
    isInitialRequest: true,
    location,
    key: offersKey,
    isLoading: false,
    staticOffer
  };
  const {
    meta: { experiment_id }
  } = offers;
  const { query, state = {} } = useSearchHistory(offersKey);
  const [results, dispatch] = useReducer(reducer, { ...initialState, read: () => initialState });

  const key = query.key ?? offersKey;

  useEffect(() => {
    dispatch({
      type: SET_RESULTS,
      payload: {
        query: { ...query, city: state.city },
        state,
        ...resultsTransformer(offers, { limit: RESULTS_LIMIT, key: offersKey }),
        key,
        location,
        staticOffer: !query?.page ? staticOffer : null
      }
    });
  }, [offers]);

  useEffect(() => {
    if (results.isInitialRequest) {
      dispatch({ type: INITIAL_LOAD });

      return;
    }

    const updateSearchResultsState = async () => {
      const type = SET_RESULTS;
      const payload = {
        query: { ...query, city: state.city },
        state,
        location
      };

      if (shouldLoad) {
        dispatch({ type, payload: { isLoading: true } });
      }

      const params = handleMultipleFields({ ...router.query });
      const city = location.city ? location.city.code : location.code;
      const queryParams = {
        ...locationToSearch(location),
        ...params,
        ...handlePathToQuery(router.query.city),
        limit: RESULTS_LIMIT,
        city,
        key,
        source: SOURCE,
        locale: locales[language.toUpperCase()] || locales.EN
      };

      try {
        const results = isResidences
          ? await residencesOffers(queryParams, profile?.id)
          : await offersAction(queryParams, experiment_id, profile?.id);

        dispatch({
          type,
          payload: {
            ...payload,
            ...resultsTransformer(results, { limit: RESULTS_LIMIT, key }),
            isLoading: false,
            staticOffer: !router.query?.page ? staticOffer : null
          }
        });
      } catch {
        dispatch({ type, payload: initialState });
      }
    };

    updateSearchResultsState();
  }, [debouncedQueryParams]);

  return <ResourcesContext.Provider value={results}>{children}</ResourcesContext.Provider>;
};

ResourcesProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
  offers: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  offersKey: PropTypes.string,
  isResidences: PropTypes.bool,
  shouldLoad: PropTypes.bool,
  staticOffer: PropTypes.object
};

export { ResourcesProvider, ResourcesContext };
